import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState } from '@ui-kit';
import { useCacheDetail } from '../hooks/useCacheDetail';
import { useCacheKeys } from '../hooks/useCacheKeys';
import { useEvictCache } from '../hooks/useEvictCache';
import { CacheTierBadge } from './CacheTierBadge';
import { EvictConfirmDialog } from './EvictConfirmDialog';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { ApiError } from '../types/cacheAdmin';

interface CacheKeyspaceDrawerProps {
  cacheName: string;
  onClose: () => void;
  onEvictSuccess: (name: string) => void;
}

/**
 * CacheKeyspaceDrawer — 캐시 키스페이스 우측 drawer (40% 폭).
 *
 * 접근성: ESC 닫기, 포커스 트랩, role="dialog".
 * 5상태: loading skeleton / empty / error / default / dialog(evict 확인)
 */
export function CacheKeyspaceDrawer({
  cacheName,
  onClose,
  onEvictSuccess,
}: CacheKeyspaceDrawerProps) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);

  const [keywordInput, setKeywordInput] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [showEvictDialog, setShowEvictDialog] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const detailQuery = useCacheDetail(cacheName);
  const keysQuery = useCacheKeys(cacheName, {
    keyword: appliedKeyword || undefined,
    page,
    size: 50,
  });
  const evictMutation = useEvictCache();

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showEvictDialog) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, showEvictDialog]);

  // 초기 포커스
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const handleEvictConfirm = () => {
    evictMutation.mutate(cacheName, {
      onSuccess: () => {
        setShowEvictDialog(false);
        showToast(t('cacheAdmin.toast.evicted', { name: cacheName }));
        onEvictSuccess(cacheName);
      },
      onError: (err) => {
        setShowEvictDialog(false);
        const apiErr = err as unknown as ApiError;
        showToast(
          t(`cacheAdmin.error.${apiErr.code}`, {
            defaultValue: t('cacheAdmin.error.network'),
          })
        );
      },
    });
  };

  const detail = detailQuery.data;
  const keysData = keysQuery.data;

  return (
    <>
      {/* 배경 오버레이 */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 800,
          background: 'rgba(0,0,0,0.25)',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer 패널 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyspace-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(40%, 560px)',
          minWidth: 320,
          zIndex: 900,
          background: 'var(--color-surface-background)',
          borderLeft: '1px solid var(--color-surface-border)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
          }}
        >
          <h2
            id="keyspace-drawer-title"
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            {t('cacheAdmin.drawer.title', { name: cacheName })}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="닫기"
            style={{
              padding: 'var(--spacing-1)',
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--font-size-lg)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* 캐시 메타 요약 */}
        {detailQuery.isLoading && (
          <div style={{ padding: 'var(--spacing-4) var(--spacing-5)' }}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: 12,
                  width: `${40 + i * 15}%`,
                  background: 'var(--color-surface-elevated)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: 'var(--spacing-2)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        )}
        {detail && (
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-5)',
              borderBottom: '1px solid var(--color-surface-border)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--spacing-2)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <div>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {t('cacheAdmin.drawer.field.tier')}:
              </span>{' '}
              <CacheTierBadge tier={detail.tier} />
            </div>
            <div>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {t('cacheAdmin.drawer.field.hitRate')}:
              </span>{' '}
              <span style={{ fontWeight: 600 }}>
                {Math.round(detail.stats.hitRate * 100)}%
              </span>{' '}
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                ({detail.stats.hitCount.toLocaleString()} / {detail.stats.missCount.toLocaleString()})
              </span>
            </div>
            <div>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {t('cacheAdmin.drawer.field.entries')}:
              </span>{' '}
              <span style={{ fontWeight: 600 }}>
                {detail.stats.estimatedSize.toLocaleString()}
                {detail.maxSize != null && ` / ${detail.maxSize.toLocaleString()}`}
              </span>
            </div>
            <div>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {t('cacheAdmin.drawer.field.ttl')}:
              </span>{' '}
              <span style={{ fontWeight: 600 }}>
                {detail.ttlSeconds != null ? formatTtl(detail.ttlSeconds) : '-'}
              </span>
            </div>
          </div>
        )}
        {/* L1 인스턴스 주의 안내 */}
        {detail?.tier === 'L1' && (
          <div
            style={{
              padding: 'var(--spacing-2) var(--spacing-5)',
              background: 'var(--color-warning-subtle, #fffbeb)',
              borderBottom: '1px solid var(--color-surface-border)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-warning-text, #92400e)',
            }}
          >
            {t('cacheAdmin.drawer.l1.localNote')}
          </div>
        )}

        {/* 키 검색 */}
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <label
            htmlFor="key-search"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            {t('cacheAdmin.drawer.keySearch')}
          </label>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <input
              id="key-search"
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedKeyword(keywordInput);
                  setPage(0);
                }
              }}
              placeholder="user:..."
              style={{
                flex: 1,
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-mono)',
              }}
            />
            <button
              type="button"
              onClick={() => {
                setAppliedKeyword(keywordInput);
                setPage(0);
              }}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              검색
            </button>
          </div>
        </div>

        {/* 키 목록 */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* loading skeleton */}
          {keysQuery.isLoading && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {[1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td style={{ padding: 'var(--spacing-3) var(--spacing-5)' }}>
                      <div
                        style={{
                          height: 12,
                          width: '70%',
                          background: 'var(--color-surface-elevated)',
                          borderRadius: 'var(--radius-sm)',
                          animation: 'pulse 1.5s ease-in-out infinite',
                        }}
                      />
                    </td>
                    <td style={{ padding: 'var(--spacing-3) var(--spacing-5)', width: 80 }}>
                      <div
                        style={{
                          height: 12,
                          width: 40,
                          background: 'var(--color-surface-elevated)',
                          borderRadius: 'var(--radius-sm)',
                          animation: 'pulse 1.5s ease-in-out infinite',
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* error */}
          {keysQuery.isError && (
            <div style={{ padding: 'var(--spacing-4)' }}>
              <ErrorState
                title={t('cacheAdmin.error.loadFailed')}
                message={
                  (keysQuery.error as unknown as ApiError)?.code === 'CACHE-503-001'
                    ? t('cacheAdmin.error.CACHE-503-001')
                    : ((keysQuery.error as unknown as ApiError)?.message ??
                      t('cacheAdmin.error.network'))
                }
                onRetry={() => void keysQuery.refetch()}
              />
            </div>
          )}

          {/* empty */}
          {!keysQuery.isLoading && !keysQuery.isError && keysData?.empty && (
            <div style={{ padding: 'var(--spacing-6)' }}>
              <EmptyState
                title={t('cacheAdmin.drawer.empty.title')}
              />
            </div>
          )}

          {/* default — 키 목록 */}
          {!keysQuery.isLoading && !keysQuery.isError && keysData && !keysData.empty && (
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
              aria-label={t('cacheAdmin.list.tableAriaLabel')}
            >
              <thead>
                <tr style={{ background: 'var(--color-surface-muted)' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2) var(--spacing-5)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {t('cacheAdmin.drawer.column.key')}
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: 'var(--spacing-2) var(--spacing-5)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      width: 80,
                    }}
                  >
                    {t('cacheAdmin.drawer.column.ttl')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {keysData.content.map((entry, idx) => (
                  <tr
                    key={`${entry.key}-${idx}`}
                    style={{ transition: 'background 0.1s' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        'var(--color-surface-muted)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = '';
                    }}
                  >
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-5)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-primary)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {entry.key}
                    </td>
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-5)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        textAlign: 'right',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      {formatTtl(entry.ttlSeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 페이지네이션 */}
        {keysData && keysData.totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-1)',
              padding: 'var(--spacing-3) var(--spacing-5)',
              borderTop: '1px solid var(--color-surface-border)',
              background: 'var(--color-surface-muted)',
            }}
          >
            <button
              type="button"
              disabled={keysData.first}
              onClick={() => setPage((p) => p - 1)}
              aria-label="이전 페이지"
              style={{
                minWidth: 28,
                height: 28,
                padding: '0 var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                fontSize: 'var(--font-size-xs)',
                cursor: keysData.first ? 'not-allowed' : 'pointer',
                opacity: keysData.first ? 0.5 : 1,
              }}
            >
              &lt;
            </button>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {keysData.number + 1} / {keysData.totalPages}
            </span>
            <button
              type="button"
              disabled={keysData.last}
              onClick={() => setPage((p) => p + 1)}
              aria-label="다음 페이지"
              style={{
                minWidth: 28,
                height: 28,
                padding: '0 var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                fontSize: 'var(--font-size-xs)',
                cursor: keysData.last ? 'not-allowed' : 'pointer',
                opacity: keysData.last ? 0.5 : 1,
              }}
            >
              &gt;
            </button>
          </div>
        )}

        {/* 푸터 — 캐시 비우기 버튼 */}
        <div
          style={{
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
          }}
        >
          <ButtonGuard requirePermission="CACHE_EVICT">
            {({ disabled, reason }) => (
              <button
                type="button"
                disabled={disabled || evictMutation.isPending}
                title={reason}
                onClick={() => setShowEvictDialog(true)}
                aria-label={`${cacheName} 캐시 비우기`}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background:
                    disabled || evictMutation.isPending
                      ? 'var(--color-surface-muted)'
                      : 'var(--color-danger)',
                  color:
                    disabled || evictMutation.isPending
                      ? 'var(--color-text-muted)'
                      : 'var(--color-text-inverse)',
                  border: `1px solid ${disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: disabled || evictMutation.isPending ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  opacity: disabled || evictMutation.isPending ? 0.6 : 1,
                }}
              >
                {evictMutation.isPending ? '...' : t('cacheAdmin.button.evict')}
              </button>
            )}
          </ButtonGuard>
        </div>
      </div>

      {/* 단건 evict 확인 다이얼로그 */}
      {showEvictDialog && (
        <EvictConfirmDialog
          mode="single"
          cacheName={cacheName}
          isPending={evictMutation.isPending}
          onConfirm={handleEvictConfirm}
          onCancel={() => setShowEvictDialog(false)}
        />
      )}

      {/* 토스트 */}
      {toast && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}

// helper defined outside to avoid closure re-definition inside component
function formatTtl(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}
